import TableContainer from 'Components/Common/TableContainerReactTable';
import React from 'react';
import { Spinner, Button, Card } from 'reactstrap';
import { formatDateTime, formatPrice, formatStringWithUnderscores, getTruncatedId } from 'lib/utils/formatters';
// import UserEditModal from '../Modal/UserEditModal';
// import { useNavigate } from 'react-router-dom';
import TransactionDetailModal from '../Modals/TransactionDetailModal';
// import UserEditModal from 'pages/Users/Modal/UserEditModal';

interface PaymentListingProps {
    data: any[];
    loading: boolean;
    isError?: boolean;
    onRefresh?: () => void;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    isRefetching?: boolean;
}

const PaymentsListing = ({
    data,
    loading,
    isError,
    onRefresh,
    currentPage,
    totalPages,
    totalCount,
    onPageChange,
    isRefetching
}: PaymentListingProps) => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [transactionId, setTransactionId] = React.useState(null);
    const [copiedId, setCopiedId] = React.useState<string | null>(null);

    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 1 second
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'Transaction ID',
            accessorKey: 'transaction_id',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const fullId = row.original?.id;
                if (!fullId) return 'N/A';

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: "Invoice NO",
            accessorKey: "invoice_id",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>
                    {getValue() || 'N/A'}
                </span>
            )
        },
        {
            header: 'Created At',
            accessorKey: 'createdAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>{formatDateTime(getValue()) || 'N/A'}</span>
            )
        },
        {
            header: 'Updated At',
            accessorKey: 'updatedAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>{formatDateTime(getValue()) || 'N/A'}</span>
            )
        },
        {
            header: 'Amount',
            accessorKey: 'earnest_money',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>
                    ${formatPrice(getValue() || 0)}
                </span>
            )
        },
        {
            header: 'Type',
            accessorKey: 'transaction_type',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const transaction = getValue();
                return (
                    <span>
                        {formatStringWithUnderscores(transaction?.type) || 'Earnest Deposit'}
                    </span>
                );
            }
        },
        {
            header: 'Status',
            accessorKey: 'status',
            enableColumnFilter: false,
            cell: (cell: any) => {
                const status = cell.getValue();
                const getStatusColor = (status: string) => {
                    switch (status?.toLowerCase()) {
                        case 'paid': return 'bg-success';
                        case 'pending': return 'bg-warning';
                        case 'failed': return 'bg-danger';
                        case 'refunded': return 'bg-info';
                        default: return 'bg-secondary';
                    }
                };
                return (
                    <span className={`badge rounded-pill ${getStatusColor(status)} text-uppercase`}>
                        {status || 'N/A'}
                    </span>
                );
            },
        },
        {
            header: "Action",
            accessorKey: "action",
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => (
                <span
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        const transactionId = row.original?.id;
                        setTransactionId(transactionId);
                        setModalOpen(true);
                    }}
                >
                    <i className="ri-eye-line" style={{ fontSize: 20, color: "#6b7280" }}></i>
                </span>
            ),
        },
    ];

    // Error state
    if (isError) {
        return (
            <div className="text-center py-5">
                <div className="text-danger mb-3">
                    <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-danger">Failed to load payments</h5>
                <p className="text-muted">Please check your connection and try again.</p>
                <Button color="primary" onClick={onRefresh}>
                    <i className="ri-refresh-line me-1"></i>
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <React.Fragment>
            <Card className="p-3">
                {loading || isRefetching ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading payments...</p>
                    </div>
                ) : (
                    <TableContainer
                        columns={columns}
                        data={data}
                        customPageSize={10}
                        tableClass="table-centered align-middle table-nowrap mb-0"
                        theadClass="text-muted table-light"
                        isServerSide={false}
                        currentPage={currentPage || 1}
                        totalPages={totalPages || 1}
                        totalCount={totalCount || data.length}
                        onPageChange={onPageChange}
                    />
                )}
                <TransactionDetailModal
                    isOpen={modalOpen}
                    toggle={() => {
                        setModalOpen(false);
                        // setTransactionId(null);
                    }}
                    transactionId={transactionId}
                />
            </Card>
        </React.Fragment>
    );
};

export default PaymentsListing;
